/**
 * Rotas da casca. Portais entram por `lazy()` — cada um é um chunk.
 * Prefixos: /pro, /marca, /empresa, /eu (A005).
 */
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, useParams } from 'react-router-dom';
import { instalarTransicoes } from '../portals/eu/shell/navegacao';
import { Center, Loader } from '@mantine/core';
import { portalIdFor, type PortalId } from '@eva/domain';
import type { BrandRole } from '@eva/schemas';
import { ehDominioDeMemorial } from '@eva/schemas';
import { EntryGate, RequirePortal, RequireSession } from './guards';
import { LoginPage } from '../pages/LoginPage';
import { ForgotPasswordPage } from '../pages/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/ResetPasswordPage';
import { ContextChooserPage } from '../pages/ContextChooserPage';
import { LegacyHandoffPage } from '../pages/LegacyHandoffPage';

const ConvitePage = lazy(() => import('../pages/ConvitePage'));
const ConvitePacientePage = lazy(() => import('../pages/ConvitePacientePage'));
const EntrarProPage = lazy(() => import('../pages/EntrarProPage'));
const LinkDaEmpresaPage = lazy(() => import('../pages/LinkDaEmpresaPage'));
// O memorial da Onda de Luz (13/10/2026): sem login, pelo hash; só para a live.
const OndaDeLuzPage = lazy(() => import('../pages/onda-de-luz/OndaDeLuzPage'));
const MemorialDaMarca = lazy(() => import('../pages/onda-de-luz/MemorialDaMarca'));
const AcompanharPage = lazy(() => import('../pages/AcompanharPage'));
const ConsentimentoPage = lazy(() => import('../pages/oauth/ConsentimentoPage'));

/*
 * Os mesmos thunks servem ao `lazy()` da rota e ao pré-carregamento antes de a
 * sessão virar `ready` (ver `prepararPortal` no SessionProvider). O Vite
 * devolve a mesma promessa para o mesmo módulo, então baixar aqui é o que
 * evita o spinner depois do login.
 */
const carregadores = {
  pro: () => import('../portals/pro'),
  marca: () => import('../portals/marca'),
  mantenedor: () => import('../portals/mantenedor'),
  empresa: () => import('../portals/empresa'),
  eu: () => import('../portals/eu'),
} satisfies Record<PortalId, () => Promise<unknown>>;

export function preloadPortal(role: BrandRole): Promise<unknown> {
  return carregadores[portalIdFor(role)]();
}

const ProPortal = lazy(carregadores.pro);
const MarcaPortal = lazy(carregadores.marca);
const MantenedorPortal = lazy(carregadores.mantenedor);
const EmpresaPortal = lazy(carregadores.empresa);
const EuPortal = lazy(carregadores.eu);
// Cadastro é público e é da paciente (A008): sequência de telas com os nomes do mobile.
const CadastroFlow = lazy(() => import('../cadastro/CadastroFlow'));
const QueroAtenderPage = lazy(() => import('../pages/QueroAtenderPage'));

function Carregando() {
  return (
    <Center h="100dvh">
      <Loader color="brand" />
    </Center>
  );
}

function LazyOutlet() {
  return (
    <Suspense fallback={<Carregando />}>
      <Outlet />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // No domínio de memorial da marca (memorial.marca.com.br), a raiz leva ao
  // memorial no ar (15/10/2026); em qualquer outro endereço, a entrada de sempre.
  {
    path: '/',
    element: ehDominioDeMemorial(window.location.hostname) ? (
      <Suspense fallback={<Carregando />}>
        <MemorialDaMarca />
      </Suspense>
    ) : (
      <EntryGate />
    ),
  },
  { path: '/entrar', element: <LoginPage /> },
  /*
   * `/entrar/<slug>` abre o login já na marca. Quem lê o slug é
   * `detectBrand`, antes do React; aqui a rota só precisa existir para o
   * caminho não cair no catch-all.
   *
   * Pendurado em `/entrar` de propósito: o primeiro segmento é fixo, então o
   * slug nunca disputa com `/eu`, `/marca` e os outros portais. O admin
   * legado detectava pelo PRIMEIRO segmento e precisava de uma lista de
   * reservados mantida por um script de build.
   */
  { path: '/entrar/:slug', element: <LoginPage /> },
  { path: '/esqueci-senha', element: <ForgotPasswordPage /> },
  { path: '/redefinir-senha', element: <ResetPasswordPage /> },
  // Aceite de convite. Pública porque quem aceita pode ainda não ter conta —
  // exigir login aqui tornaria o fluxo impossível.
  {
    path: '/convite/:token',
    element: (
      <Suspense fallback={<Carregando />}>
        <ConvitePage />
      </Suspense>
    ),
  },
  // Convite de paciente existente (A052): a marca convidou, quem aceita é a
  // dona da conta. Pública — chega pelo link do e-mail, sem sessão.
  {
    path: '/convite-paciente/:token',
    element: (
      <Suspense fallback={<Carregando />}>
        <ConvitePacientePage />
      </Suspense>
    ),
  },
  // Link de convite para profissionais (A037). Pública pelo mesmo motivo do
  // convite: quem chega ainda não tem conta.
  {
    path: '/entrar-pro/:token',
    element: (
      <Suspense fallback={<Carregando />}>
        <EntrarProPage />
      </Suspense>
    ),
  },
  // Convite de acesso compartilhado (A062, fase E). Pública: quem chega pelo
  // link ainda não tem conta.
  {
    path: '/acompanhar/:token',
    element: (
      <Suspense fallback={<Carregando />}>
        <AcompanharPage />
      </Suspense>
    ),
  },
  // Links antigos do convite (`/register?...&inviteToken=`), de antes da
  // fase E: o app web nunca teve `/register`, e o link caía no painel.
  { path: '/register', element: <RedirecionarConviteAntigo /> },
  // Link de cadastro de uma empresa (A062). Pública: a colaboradora chega
  // pelo link que o RH divulgou, ou digitou o código no cadastro.
  {
    path: '/e/:codigo',
    element: (
      <Suspense fallback={<Carregando />}>
        <LinkDaEmpresaPage />
      </Suspense>
    ),
  },
  // O memorial das experiências especiais (A160), sem login, pelo hash.
  {
    path: '/experiencias/:hash',
    element: (
      <Suspense fallback={<Carregando />}>
        <OndaDeLuzPage />
      </Suspense>
    ),
  },
  // O endereço do primeiro deploy (13/10/2026): links já copiados seguem abrindo.
  { path: '/eventos/:hash', element: <RedirecionarMemorialAntigo /> },
  // Quem quer atender e chegou pela porta (A039): explica que é por convite.
  // Antes de `/cadastro/*` para não cair no fluxo de cadastro de paciente.
  {
    path: '/cadastro/profissional',
    element: (
      <Suspense fallback={<Carregando />}>
        <QueroAtenderPage />
      </Suspense>
    ),
  },
  {
    path: '/cadastro/*',
    element: (
      <Suspense fallback={<Carregando />}>
        <CadastroFlow />
      </Suspense>
    ),
  },
  {
    element: <RequireSession />,
    children: [
      { path: '/contextos', element: <ContextChooserPage /> },
      { path: '/legado', element: <LegacyHandoffPage /> },
      // Consentimento do conector MCP (M2b): a IA da marca manda para cá pelo
      // /oauth/authorize da API. Só precisa de sessão — não de um contexto
      // assumido, então fica fora dos portais e vale também em `choosing`.
      {
        path: '/oauth/consentimento',
        element: (
          <Suspense fallback={<Carregando />}>
            <ConsentimentoPage />
          </Suspense>
        ),
      },
      {
        element: <LazyOutlet />,
        children: [
          { path: '/mantenedor/*', element: <RequirePortal portal="mantenedor"><MantenedorPortal /></RequirePortal> },
          { path: '/pro/*', element: <RequirePortal portal="pro"><ProPortal /></RequirePortal> },
          { path: '/empresa/*', element: <RequirePortal portal="empresa"><EmpresaPortal /></RequirePortal> },
          { path: '/eu/*', element: <RequirePortal portal="eu"><EuPortal /></RequirePortal> },
          // O portal da marca é o painel inteiro, nas URLs de sempre
          // (/dashboard, /users, /content/...), num chunk só (A019). O
          // roteador ordena por especificidade, então /pro, /empresa e /eu
          // continuam ganhando deste curinga.
          { path: '/*', element: <RequirePortal portal="marca"><MarcaPortal /></RequirePortal> },
        ],
      },
    ],
  },
  { path: '/login', element: <Navigate to="/entrar" replace /> },
]);

// As transições entre as telas da paciente decidem o movimento por aqui,
// interceptando o `navigate` do roteador (`portals/eu/shell/navegacao.ts`).
instalarTransicoes(router);


/** `/register?inviteToken=...` (convite antigo) → `/acompanhar/<token>`; sem token, o cadastro. */
function RedirecionarConviteAntigo() {
  const token = new URLSearchParams(window.location.search).get('inviteToken');
  return <Navigate to={token ? `/acompanhar/${encodeURIComponent(token)}` : '/cadastro'} replace />;
}

function RedirecionarMemorialAntigo() {
  const { hash = '' } = useParams();
  return <Navigate to={`/experiencias/${encodeURIComponent(hash)}`} replace />;
}
